import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { AHeader, ACard, btnPrimary, inputDark } from "@/components/shared/AdminKit";
import { CreateAgentDialog } from "@/components/agents/CreateAgentDialog";
import { StatusBadge } from "@/components/agents/AgentFields";
import { useTr, fmtDate } from "@/lib/tr";
import { Loader2, UserPlus, Eye, Ban, RotateCcw, PauseCircle, Search, ChevronLeft, ChevronRight, Mail } from "lucide-react";

export function useAgentStatus(reload) {
  const tr = useTr();
  const confirm = useConfirm();
  return async (a, status) => {
    const msgs = {
      suspended: tr("Suspendre ce compte ? Ses sessions seront révoquées immédiatement.", "Suspend this account? Its sessions are revoked immediately."),
      disabled: tr("Désactiver ce compte ? L'agent ne pourra plus se connecter. L'historique est conservé.", "Disable this account? The agent can no longer log in. History is kept."),
      active: tr("Réactiver ce compte ?", "Reactivate this account?"),
    };
    const ok = await confirm({ title: a.name, description: msgs[status], confirmLabel: tr("Confirmer", "Confirm"), cancelLabel: tr("Annuler", "Cancel"), danger: status !== "active" });
    if (!ok) return;
    try { await api.post(`/admin/agents/${a.id}/status`, { status }); toast.success(tr("Statut mis à jour", "Status updated")); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
}

function Row({ a, tr, onStatus }) {
  const btn = "rounded-lg border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800";
  return (
    <tr className="border-t border-slate-800 text-sm" data-testid={`agent-row-${a.id}`}>
      <td className="px-4 py-3"><Link to={`/admin/agents/${a.id}`} className="font-medium text-white hover:text-accent" data-testid={`agent-name-${a.id}`}>{a.name}</Link><div className="text-xs text-slate-500">{a.email}</div></td>
      <td className="px-4 py-3 text-slate-300">{a.phone || "—"}</td>
      <td className="px-4 py-3 text-slate-300">{a.job_title}<div className="text-xs text-slate-500">{a.department || ""}</div></td>
      <td className="px-4 py-3"><StatusBadge tr={tr} status={a.status} testid={`agent-status-${a.id}`} />{!a.email_verified && <div className="mt-1 text-[11px] text-amber-300">{tr("E-mail non vérifié", "Email not verified")}</div>}</td>
      <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(a.last_login, tr.lang)}</td>
      <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(a.last_activity, tr.lang)}</td>
      <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(a.created_at, tr.lang)}</td>
      <td className="px-4 py-3"><div className="flex gap-1.5">
        <Link to={`/admin/agents/${a.id}`} className={btn} title={tr("Consulter / modifier", "View / edit")} data-testid={`agent-view-${a.id}`}><Eye className="h-4 w-4" /></Link>
        {a.status === "active" && <button className={btn} onClick={() => onStatus(a, "suspended")} title={tr("Suspendre", "Suspend")} data-testid={`agent-suspend-${a.id}`}><PauseCircle className="h-4 w-4 text-amber-400" /></button>}
        {a.status !== "active" && <button className={btn} onClick={() => onStatus(a, "active")} title={tr("Réactiver", "Reactivate")} data-testid={`agent-reactivate-${a.id}`}><RotateCcw className="h-4 w-4 text-emerald-400" /></button>}
        {a.status !== "disabled" && <button className={btn} onClick={() => onStatus(a, "disabled")} title={tr("Désactiver", "Disable")} data-testid={`agent-disable-${a.id}`}><Ban className="h-4 w-4 text-red-400" /></button>}
      </div></td>
    </tr>
  );
}

function Filters({ tr, q, setQ, status, setStatus, dept, setDept, departments }) {
  return (
    <div className="mb-4 flex flex-wrap gap-3">
      <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Rechercher nom, e-mail, fonction…", "Search name, email, job…")} data-testid="agents-search" className={inputDark + " pl-9"} /></div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="agents-filter-status" className={inputDark + " sm:!w-auto"}>
        <option value="">{tr("Tous les statuts", "All statuses")}</option>
        <option value="active">{tr("Actif", "Active")}</option><option value="suspended">{tr("Suspendu", "Suspended")}</option><option value="disabled">{tr("Désactivé", "Disabled")}</option>
      </select>
      <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="agents-filter-department" className={inputDark + " sm:!w-auto"}>
        <option value="">{tr("Tous les départements", "All departments")}</option>
        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  );
}

export default function AdminAgents() {
  const tr = useTr();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(""); const [status, setStatus] = useState(""); const [dept, setDept] = useState(""); const [page, setPage] = useState(1);
  const load = useCallback(() => {
    const ctrl = new AbortController();
    api.get("/admin/agents", { signal: ctrl.signal, params: { search: q || undefined, status: status || undefined, department: dept || undefined, page, page_size: 20 } })
      .then((r) => setData(r.data)).catch((e) => { if (e.code === "ERR_CANCELED") return; toast.error(apiError(e.response?.data?.detail)); setData({ items: [], total: 0, departments: [] }); });
    return ctrl;
  }, [q, status, dept, page]);
  useEffect(() => { let ctrl; const id = setTimeout(() => { ctrl = load(); }, 250); return () => { clearTimeout(id); if (ctrl) ctrl.abort(); }; }, [load]);
  useEffect(() => {
    api.get("/admin/rbac/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/admin/agents", { params: { page_size: 100 } }).then((r) => setManagers(r.data.items)).catch(() => {});
  }, []);
  const onStatus = useAgentStatus(load);
  const [sending, setSending] = useState(false);
  const sendSummary = async () => {
    setSending(true);
    try { const { data } = await api.post("/admin/agents/weekly-summary/send"); toast.success(tr(`Résumé des 7 derniers jours envoyé à ${data.recipients} Super Admin`, `Last-week summary sent to ${data.recipients} Super Admin`)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;
  return (
    <div data-testid="admin-agents-page">
      <AHeader title={tr("Agents", "Agents")} subtitle={tr("Comptes professionnels des administrateurs délégués de TWP.", "Professional accounts of TWP delegated administrators.")}
        action={<div className="flex flex-wrap gap-2">
          <button onClick={sendSummary} disabled={sending} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50" data-testid="send-weekly-summary-btn">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {tr("Envoyer le résumé hebdo", "Send weekly summary")}</button>
          <button onClick={() => setOpen(true)} className={btnPrimary} data-testid="create-agent-btn"><UserPlus className="h-4 w-4" /> {tr("Créer un agent", "Create an agent")}</button>
        </div>} />
      <Filters tr={tr} q={q} setQ={(v) => { setQ(v); setPage(1); }} status={status} setStatus={(v) => { setStatus(v); setPage(1); }} dept={dept} setDept={(v) => { setDept(v); setPage(1); }} departments={data?.departments || []} />
      <ACard>
        {!data ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : !data.items.length ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="agents-empty">{tr("Aucun agent pour le moment.", "No agents yet.")}</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full min-w-[980px] text-left">
            <thead className="text-xs uppercase tracking-wide text-slate-500"><tr>
              {[tr("Agent", "Agent"), tr("Téléphone", "Phone"), tr("Fonction", "Job"), tr("Statut", "Status"), tr("Dernière connexion", "Last login"), tr("Dernière activité", "Last activity"), tr("Créé le", "Created"), ""].map((h, i) => <th key={i} className="px-4 py-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>{data.items.map((a) => <Row key={a.id} a={a} tr={tr} onStatus={onStatus} />)}</tbody>
          </table></div>
        )}
      </ACard>
      {data && data.total > 20 && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm text-slate-400" data-testid="agents-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-slate-700 p-1.5 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          {page} / {pages}
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-lg border border-slate-700 p-1.5 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      <CreateAgentDialog open={open} onOpenChange={setOpen} catalog={catalog} managers={managers} canSensitive={user?.role === "super_admin"} onCreated={load} />
    </div>
  );
}
